import { useState } from 'react';
import {
  InvoiceQueryReason,
  InvoiceQueryReasonLabels,
  IOpenInvoiceQueryRequest,
} from 'shared';

import { Button } from '../../../common/Atoms/Button';
import { DatePicker } from '../../../common/Atoms/DatePicker';
import { Select } from '../../../common/Atoms/Select';
import { TextArea } from '../../../common/Atoms/TextArea';

const reasonOptions = Object.values(InvoiceQueryReason).map(value => ({
  label: InvoiceQueryReasonLabels[value],
  value,
}));

interface InvoiceQueryFormProps {
  onSubmit: (query: IOpenInvoiceQueryRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  submitLabel?: string;
}

/** Record an invoice query. Shared by the invoice page and escalations. */
export function InvoiceQueryForm({
  onSubmit,
  onCancel,
  loading,
  submitLabel = `Open query`,
}: InvoiceQueryFormProps) {
  const [reason, setReason] = useState<InvoiceQueryReason | null>(null);
  const [detail, setDetail] = useState<string>(``);
  const [nextAction, setNextAction] = useState<string>(``);
  const [dueDate, setDueDate] = useState<string>(``);

  const detailMissing = reason === InvoiceQueryReason.OTHER && !detail.trim();
  const canSubmit = !!reason && !detailMissing && !loading;

  async function submit() {
    if (!reason) return;

    await onSubmit({
      reason,
      detail: detail.trim() || null,
      nextAction: nextAction.trim() || null,
      dueDate: dueDate || null,
    });
  }

  return (
    <div className={ `space-y-3` }>
      <Select
        label={ `Reason` }
        name={ `reason` }
        options={ reasonOptions }
        selected={ reason }
        onChange={ ({ value }) => setReason(value) }
      />
      <TextArea
        label={ reason === InvoiceQueryReason.OTHER ? `Details (required)` : `Details` }
        name={ `detail` }
        value={ detail }
        rows={ 3 }
        onChange={ ({ value }) => setDetail(String(value)) }
      />
      <TextArea
        label={ `Next action` }
        name={ `nextAction` }
        value={ nextAction }
        rows={ 2 }
        onChange={ ({ value }) => setNextAction(String(value)) }
        placeholder={ `e.g. Check the delivery note with the customer` }
      />
      <DatePicker
        label={ `Due date` }
        name={ `dueDate` }
        value={ dueDate || null }
        onChange={ ({ value }) => setDueDate(value || ``) }
      />
      <div className={ `flex justify-end gap-2` }>
        <Button color={ `none` }
          onClick={ onCancel }
          disabled={ loading }>
          { `Cancel` }
        </Button>
        <Button onClick={ submit }
          loading={ loading }
          disabled={ !canSubmit }>
          { submitLabel }
        </Button>
      </div>
    </div>
  );
}
